"use client";

import {
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	RichText,
	VideoPlayer
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import { useId } from "react";

import * as styles from "./ProductFiftyFifty.module.scss";

interface Asset<ImageComponentProps> {
	type: "image" | "video";
	image: ImageProps<ImageComponentProps>;
	video?: string;
}

export interface ProductFiftyFiftyProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	headline?: HeadingProps;
	description?: string;
	cta?: LinkProps<LinkComponentProps>;
	assets?: Asset<ImageComponentProps>[];
	theme: "image-left" | "image-right";
}

/**
 * Product 50-50 component
 */
export function ProductFiftyFifty<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	headline,
	description,
	cta,
	assets,
	theme
}: ProductFiftyFiftyProps<
	ImageComponentProps,
	LinkComponentProps
>): JSX.Element {
	const id = useId();
	const headingId = `product-fifty-fifty-${id}`;
	const assetType =
		assets?.length && assets.length > 1 ? "multiple" : "single";

	const assetAspectRatio = assetType === "single" ? "16:9" : "1:1";

	let contentColumns = [
		<div
			{...classNameHelper(styles["column"], styles["content"])}
			key="productFiftyFity_0"
		>
			{headline && (
				<Heading
					{...headline}
					id={headingId}
					styledAs="h2"
					{...classNameHelper(styles["headline"], headline.className)}
				/>
			)}
			{description ? (
				<RichText
					content={description}
					className={styles["description"]}
				/>
			) : null}
			{cta && (
				<Link<LinkComponentProps>
					{...cta}
					{...classNameHelper(styles["cta"], cta.className)}
					kind="secondary"
				/>
			)}
			,
		</div>,
		<div
			{...classNameHelper(
				styles["column"],
				styles["assets"],
				styles[assetType]
			)}
			key="productFiftyFity_1"
		>
			{assets?.map((asset, idx) =>
				asset.type === "image" ? (
					// @ts-expect-error - React 19 generic type limitation with Image component
					<Image<ImageComponentProps>
						{...asset.image}
						key={idx}
						aspectRatio={assetAspectRatio}
						objectFit="cover"
						alt={asset.image.alt || ""}
					/>
				) : (
					<VideoPlayer
						key={idx}
						videoSrc={asset.video as string}
						posterSrc={asset.image.src}
					/>
				)
			)}
		</div>
	];

	if (theme === "image-left") {
		contentColumns = contentColumns.reverse();
	}

	return (
		<article aria-labelledby={headingId} className={styles["container"]}>
			{contentColumns}
		</article>
	);
}
